(function () {
  const SLIDE_FADE_MS = 700;
  const VIDEO_END_THRESHOLD_S = 0.05;
  const HTML_VIDEO_SELECTOR = "video.media-content-slider__feature-media-video";
  const IFRAME_VIDEO_SELECTOR =
    "iframe.media-content-slider__feature-media-video--iframe";

  class MediaContentSlider {
    constructor(el) {
      this.el = el;
      this.navItems = Array.from(
        el.querySelectorAll(".media-content-slider__nav-item")
      );
      this.bullets = Array.from(
        el.querySelectorAll(".media-content-slider__bullet")
      );
      this.visuals = Array.from(
        el.querySelectorAll(".media-content-slider__visual")
      );
      this.total = this.visuals.length;

      if (this.total <= 1) return;

      this.current = 0;
      this.autoplay = el.dataset.autoplay === "true";
      this.autoplayDelay = parseInt(el.dataset.autoplayDelay, 10) || 6000;
      this.matchVideoDuration = el.dataset.matchVideoDuration === "true";
      this.raf = null;
      this.startTime = null;
      this.timerElapsedMs = 0;
      this.timerDelayMs = 0;
      this.progressRunId = 0;
      this.isInViewport = false;
      this.intersectionObserver = null;
      this._videoProgressCleanup = null;
      this._leaveTimer = null;
      this.eventController = new AbortController();
      this.focusHeld = false;

      // Keyboard focus holds the autoplay: the next slide would hide the panel
      // holding the focus. A mouse click focuses a tab too, so only :focus-visible
      // counts.
      el.addEventListener(
        "focusin",
        (event) => {
          if (!event.target.matches(":focus-visible")) return;
          this.focusHeld = true;
          this._stopProgress(true, true);
        },
        { signal: this.eventController.signal }
      );
      el.addEventListener(
        "focusout",
        (event) => {
          if (!this.focusHeld || el.contains(event.relatedTarget)) return;
          this.focusHeld = false;
          this._startProgress({ resume: true });
        },
        { signal: this.eventController.signal }
      );

      this.navItems.forEach((item, i) => {
        item.addEventListener("click", () => this._goTo(i), {
          signal: this.eventController.signal,
        });
      });

      this.bullets.forEach((btn, i) => {
        btn.addEventListener(
          "click",
          (e) => {
            e.stopPropagation();
            this._goTo(i);
          },
          {
            signal: this.eventController.signal,
          }
        );

        btn.addEventListener(
          "keydown",
          (event) => {
            this._handleTabKeydown(event, i);
          },
          {
            signal: this.eventController.signal,
          }
        );
      });

      this._updateBulletStates(this.current);
      this._scrollNavItemIntoView(this.current, { instant: true });
      this._setupIntersectionObserver();

      this.visuals.forEach((visual, i) => {
        if (i !== this.current) this._pauseSlideMedia(visual);
      });
    }

    destroy() {
      this._stopProgress();
      this.eventController.abort();
      this._clearLeaveTimer();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
    }

    _clearLeaveTimer() {
      if (this._leaveTimer) {
        clearTimeout(this._leaveTimer);
        this._leaveTimer = null;
      }
    }

    _handleTabKeydown(event, index) {
      let nextIndex = null;

      if (event.key === "ArrowDown" || event.key === "ArrowRight") {
        nextIndex = (index + 1) % this.total;
      }

      if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
        nextIndex = (index - 1 + this.total) % this.total;
      }

      if (event.key === "Home") {
        nextIndex = 0;
      }

      if (event.key === "End") {
        nextIndex = this.total - 1;
      }

      if (nextIndex === null) return;

      event.preventDefault();
      this._goTo(nextIndex);
      this.bullets[nextIndex]?.focus();
    }

    _getActiveHtmlVideo() {
      const visual = this.visuals[this.current];
      if (!visual) return null;
      return visual.querySelector(HTML_VIDEO_SELECTOR);
    }

    _pauseSlideMedia(visual, { reset = true } = {}) {
      if (!visual) return;

      visual.querySelectorAll(HTML_VIDEO_SELECTOR).forEach((video) => {
        video.pause();
        if (reset) {
          video.currentTime = 0;
        }
      });

      visual.querySelectorAll(IFRAME_VIDEO_SELECTOR).forEach((iframe) => {
        this._pauseIframe(iframe);
      });
    }

    _resumeSlideMedia(visual) {
      if (!visual) return;

      visual.querySelectorAll(HTML_VIDEO_SELECTOR).forEach((video) => {
        video.play().catch(() => {});
      });

      visual.querySelectorAll(IFRAME_VIDEO_SELECTOR).forEach((iframe) => {
        this._playIframe(iframe);
      });
    }

    _restartSlideMedia(visual) {
      if (!visual) return;

      visual.querySelectorAll(HTML_VIDEO_SELECTOR).forEach((video) => {
        video.loop = !this.matchVideoDuration;
        video.currentTime = 0;
        video.play().catch(() => {});
      });

      visual.querySelectorAll(IFRAME_VIDEO_SELECTOR).forEach((iframe) => {
        this._restartIframe(iframe);
      });
    }

    _pauseIframe(iframe) {
      const src = iframe.src;

      if (src.includes("youtube.com")) {
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "*"
        );
        return;
      }

      if (src.includes("player.vimeo.com")) {
        iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
      }
    }

    _restartIframe(iframe) {
      const src = iframe.getAttribute("src");
      if (!src) return;
      iframe.setAttribute("src", src);
    }

    _playIframe(iframe) {
      const src = iframe.src;

      if (src.includes("youtube.com")) {
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          "*"
        );
        return;
      }

      if (src.includes("player.vimeo.com")) {
        iframe.contentWindow?.postMessage('{"method":"play"}', "*");
      }
    }

    /* ── Navigation ── */

    _goTo(index) {
      if (index === this.current) return;
      this._stopProgress();
      this._clearLeaveTimer();

      const prev = this.current;
      this.current = index;
      const prevVisual = this.visuals[prev];
      const nextVisual = this.visuals[index];

      this.visuals.forEach((visual) => {
        visual.classList.remove("is-leaving");
      });

      /* Only the media panel transitions */
      prevVisual.classList.remove("is-active");
      prevVisual.classList.add("is-leaving");
      nextVisual.classList.remove("is-leaving");
      nextVisual.classList.add("is-active");
      prevVisual.setAttribute("aria-hidden", "true");
      nextVisual.setAttribute("aria-hidden", "false");

      this._leaveTimer = setTimeout(() => {
        prevVisual.classList.remove("is-leaving");
        this._leaveTimer = null;
      }, SLIDE_FADE_MS);

      this._pauseSlideMedia(prevVisual, { reset: true });
      this._restartSlideMedia(nextVisual);

      /* Update nav items (opacity) */
      this.navItems[prev].classList.remove("is-active");
      this.navItems[index].classList.add("is-active");

      this._updateBulletStates(index);
      this._scrollNavItemIntoView(index);

      if (this.autoplay && this.isInViewport) this._startProgress();
    }

    _updateBulletStates(activeIndex) {
      this.bullets.forEach((btn, i) => {
        const isActive = i === activeIndex;
        const isDone = i < activeIndex;

        btn.classList.toggle("is-active", isActive);
        btn.classList.toggle("is-done", isDone);
        btn.setAttribute("aria-current", isActive ? "true" : "false");
        btn.setAttribute("aria-selected", isActive ? "true" : "false");
        btn.setAttribute("tabindex", isActive ? "0" : "-1");

        const fill = btn.querySelector(".media-content-slider__bullet-fill");
        if (!fill) return;

        if (isDone) {
          fill.style.removeProperty("transform");
          return;
        }

        if (!isActive) {
          fill.style.transform = this._fillTransform(0);
        }
      });

      const activeBullet = this.bullets[activeIndex];
      if (activeBullet) {
        this._setFill(activeBullet, 0);
      }
    }

    _scrollNavItemIntoView(index, { instant = false } = {}) {
      const nav = this.el.querySelector(".media-content-slider__nav");
      const target = this.navItems[index];
      if (!nav || !target) return;
      if (nav.scrollWidth <= nav.clientWidth + 1) return;

      target.scrollIntoView({
        behavior: instant ? "auto" : "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }

    _next() {
      this._goTo((this.current + 1) % this.total);
    }

    _advanceFromVideoEnd(runId) {
      if (!this.matchVideoDuration) return;
      if (runId !== this.progressRunId) return;

      const video = this._getActiveHtmlVideo();
      if (video) {
        video.loop = false;
        if (Number.isFinite(video.duration) && video.duration > 0) {
          video.currentTime = Math.max(video.duration - 0.001, 0);
        }
        video.pause();
      }

      this._stopProgress(false);
      this._next();
    }

    /* ── Progress animation ── */

    _startProgress({ resume = false } = {}) {
      if (!this.autoplay || !this.isInViewport) return;

      this._stopProgress();
      if (!resume) {
        this._updateBulletStates(this.current);
      }
      if (this.focusHeld) return;

      const video = this._getActiveHtmlVideo();
      if (this.matchVideoDuration && video) {
        if (!resume) {
          this.timerElapsedMs = 0;
          this.timerDelayMs = 0;
        }
        this._startVideoSyncedProgress(video);
        return;
      }

      this._startTimerProgress({ resume });
    }

    _startVideoSyncedProgress(video) {
      if (!this.matchVideoDuration) {
        this._startTimerProgress();
        return;
      }

      const runId = ++this.progressRunId;
      const bullet = this.bullets[this.current];
      if (!bullet) return;

      const beginSync = () => {
        if (runId !== this.progressRunId) return;
        if (!Number.isFinite(video.duration) || video.duration <= 0) {
          this._startTimerProgress();
          return;
        }

        video.loop = false;

        const onTimeUpdate = () => {
          if (runId !== this.progressRunId) return;

          const progress = Math.min(video.currentTime / video.duration, 1);
          this._setFill(bullet, progress);

          const remaining = video.duration - video.currentTime;
          if (remaining <= VIDEO_END_THRESHOLD_S) {
            this._advanceFromVideoEnd(runId);
          }
        };

        const onEnded = () => {
          this._advanceFromVideoEnd(runId);
        };

        video.addEventListener("timeupdate", onTimeUpdate);
        video.addEventListener("ended", onEnded);

        this._videoProgressCleanup = () => {
          video.removeEventListener("timeupdate", onTimeUpdate);
          video.removeEventListener("ended", onEnded);
        };

        if (video.paused) {
          video.play().catch(() => {});
        }

        onTimeUpdate();
      };

      if (
        video.readyState >= 1 &&
        Number.isFinite(video.duration) &&
        video.duration > 0
      ) {
        beginSync();
        return;
      }

      const onReady = () => beginSync();
      video.addEventListener("loadedmetadata", onReady, { once: true });
      this._videoProgressCleanup = () => {
        video.removeEventListener("loadedmetadata", onReady);
      };
    }

    _startTimerProgress({ resume = false } = {}) {
      const currentDelay = this.matchVideoDuration
        ? this._getCurrentSlideDelay()
        : this.autoplayDelay;
      const runId = ++this.progressRunId;
      const bullet = this.bullets[this.current];
      if (!bullet) return;

      if (!resume) {
        this.timerElapsedMs = 0;
        this.timerDelayMs = currentDelay;
      } else if (!this.timerDelayMs) {
        this.timerDelayMs = currentDelay;
      }

      const effectiveDelay = this.timerDelayMs || currentDelay;

      this.startTime = null;
      const tick = (timestamp) => {
        if (runId !== this.progressRunId) return;

        if (this.startTime === null) {
          this.startTime = timestamp - this.timerElapsedMs;
        }

        this.timerElapsedMs = Math.min(
          Math.max(timestamp - this.startTime, 0),
          effectiveDelay
        );

        const progress = Math.min(this.timerElapsedMs / effectiveDelay, 1);
        this._setFill(bullet, progress);

        if (progress < 1) {
          this.raf = requestAnimationFrame(tick);
          return;
        }

        this.timerElapsedMs = 0;
        this.timerDelayMs = 0;

        this._next();
      };

      this.raf = requestAnimationFrame(tick);
    }

    _stopProgress(clearVideoListeners = true, preserveTimerProgress = false) {
      this.progressRunId += 1;

      if (
        preserveTimerProgress &&
        this.startTime !== null &&
        this.timerDelayMs
      ) {
        this.timerElapsedMs = Math.min(
          Math.max(performance.now() - this.startTime, 0),
          this.timerDelayMs
        );
      }

      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
      this.startTime = null;

      if (!preserveTimerProgress) {
        this.timerElapsedMs = 0;
        this.timerDelayMs = 0;
      }

      if (clearVideoListeners && this._videoProgressCleanup) {
        this._videoProgressCleanup();
        this._videoProgressCleanup = null;
      }
    }

    _fillTransform(progress) {
      return `scaleY(${progress})`;
    }

    _setFill(bullet, progress) {
      if (!bullet?.classList.contains("is-active")) return;

      const fill = bullet.querySelector(".media-content-slider__bullet-fill");
      if (!fill) return;

      fill.style.transform = this._fillTransform(progress);
    }

    _setupIntersectionObserver() {
      if (!this.autoplay) return;

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const visible =
            !!entry && entry.isIntersecting && entry.intersectionRatio >= 0.2;

          if (visible === this.isInViewport) return;
          this.isInViewport = visible;

          if (this.isInViewport) {
            this._resumeSlideMedia(this.visuals[this.current]);
            this._startProgress({ resume: true });
          } else {
            this._stopProgress(true, true);
            this._pauseSlideMedia(this.visuals[this.current], {
              reset: false,
            });
          }
        },
        {
          threshold: [0, 0.2, 1],
        }
      );

      this.intersectionObserver.observe(this.el);
    }

    _getCurrentSlideDelay() {
      if (!this.matchVideoDuration) return this.autoplayDelay;

      const currentVisual = this.visuals[this.current];
      if (!currentVisual) return this.autoplayDelay;

      let durationSeconds = parseFloat(
        currentVisual.dataset.videoDuration || "0"
      );
      if (!Number.isFinite(durationSeconds)) durationSeconds = 0;

      const htmlVideo = currentVisual.querySelector(HTML_VIDEO_SELECTOR);
      if (
        htmlVideo &&
        Number.isFinite(htmlVideo.duration) &&
        htmlVideo.duration > 0
      ) {
        durationSeconds = Math.max(durationSeconds, htmlVideo.duration);
      }

      if (durationSeconds > 1000) {
        durationSeconds = durationSeconds / 1000;
      }

      if (durationSeconds <= 0) return this.autoplayDelay;

      let remainingSeconds = durationSeconds;

      if (
        htmlVideo &&
        Number.isFinite(htmlVideo.currentTime) &&
        htmlVideo.currentTime >= 0 &&
        htmlVideo.currentTime < durationSeconds
      ) {
        remainingSeconds = Math.max(durationSeconds - htmlVideo.currentTime, 0);
      }

      const videoBasedDelay = Math.max(
        Math.ceil(remainingSeconds * 1000 - SLIDE_FADE_MS),
        0
      );

      return videoBasedDelay;
    }
  }

  function initSection(section) {
    if (
      !section ||
      !section?.classList.contains("media-content-slider-section")
    ) {
      return;
    }

    const root = section.querySelector(".media-content-slider");
    if (!root) return;

    if (root._mediaContentInstance) {
      root._mediaContentInstance.destroy();
      root._mediaContentInstance = null;
    }
    root._mediaContentInstance = new MediaContentSlider(root);

    // Fixed an issue where placeholders in other sections were not displayed because similar SVGs were in this section with visibility hidden.
    const placeholderSvgs = root.querySelectorAll(".placeholder-svg");
    placeholderSvgs.forEach((svg) => {
      svg.querySelectorAll("defs").forEach((defs) => {
        defs.remove();
      });
    });
  }

  initSection(document.currentScript.parentElement);

  if (!window.__themeMediaContentSliderEvents) {
    window.__themeMediaContentSliderEvents = true;

    document.addEventListener("shopify:section:load", function (event) {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (e) => {
      e.target.querySelectorAll(".media-content-slider").forEach((el) => {
        if (!el._mediaContentInstance) return;

        el._mediaContentInstance.destroy();
        el._mediaContentInstance = null;
      });
    });
  }
})();
